// ConversationsContext.jsx
// Responsibility: Managing the list of all chat conversations
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as apiChat from '../api/chat';
import { useAuth } from './AuthContext';

const ConversationsContext = createContext();

export const ConversationsProvider = ({ children }) => {
    // --- State ---
    const [conversations, setConversations] = useState([]);
    const [isLoadingConversations, setIsLoadingConversations] = useState(false);
    const [conversationsError, setConversationsError] = useState(null);
    
    // Get auth context
    const { userId, isSessionValid, setAuthError, setIsSessionValid } = useAuth();

    // --- User-Scoped LocalStorage with Stale-While-Revalidate ---
    useEffect(() => {
        if (!userId) {
            console.log('ConversationsContext: No userId, skipping conversation load');
            setConversations([]);
            return;
        }

        // Implementation of "Stale-While-Revalidate" caching strategy
        const loadConversationsWithCache = async () => {
            const storageKey = `chatHistory_${userId}`;
            
            // Step 1: IMMEDIATE - Load from localStorage for instant UI
            const storedConversations = localStorage.getItem(storageKey);
            if (storedConversations) {
                try {
                    const parsed = JSON.parse(storedConversations);
                    setConversations(parsed);
                    console.log(`ConversationsContext: Loaded ${parsed.length} cached conversations for user ${userId}`);
                } catch (error) {
                    console.error('ConversationsContext: Failed to parse stored conversations:', error);
                    localStorage.removeItem(storageKey); // Clear corrupted data
                    setConversations([]);
                }
            } else {
                console.log(`ConversationsContext: No cached conversations for user ${userId}`);
                setConversations([]);
            }
            
            // Step 2: BACKGROUND - Fetch fresh data from API
            try {
                setIsLoadingConversations(true);
                setConversationsError(null);
                
                console.log(`ConversationsContext: Fetching fresh conversation list from API for user ${userId}`);
                const freshConversations = await apiChat.fetchConversations();
                
                // Map the backend property names to frontend expected names
                const mappedConversations = freshConversations.map(conv => ({
                    id: conv.conversation_id,
                    title: conv.title || 'Untitled Chat',
                    lastUpdated: conv.last_updated_at,
                    createdAt: conv.created_at,
                    messageCount: conv.message_count || 0,
                    fileCount: conv.file_count || 0,
                    firstAssistantPreview: conv.first_assistant_preview || '',
                    userId: userId
                }));
                
                // Step 3: UPDATE - Update state with fresh data
                setConversations(mappedConversations);
                console.log(`ConversationsContext: Updated with ${mappedConversations.length} fresh conversations from API`);
                
                // Step 4: CACHE - Write fresh data back to localStorage
                localStorage.setItem(storageKey, JSON.stringify(mappedConversations));
                console.log(`ConversationsContext: Cached fresh conversation list for user ${userId}`);
                
            } catch (error) {
                console.error('ConversationsContext: Failed to fetch conversations from API:', error);
                setConversationsError('Failed to sync conversations');
                
                // Handle authentication errors
                if (error.message?.includes('Authentication') || 
                    error.message?.includes('401') ||
                    error.message?.includes('Unauthorized')) {
                    setAuthError('Session expired. Please sign in again.');
                    setIsSessionValid(false);
                }
                
                // Silent fail - we already have cached data displayed
                // The error state can be used to show a sync failure indicator in UI
            } finally {
                setIsLoadingConversations(false);
            }
        };

        loadConversationsWithCache();
    }, [userId, setAuthError, setIsSessionValid]);

    // --- Save Conversation Metadata ---
    const saveConversation = useCallback((convoToSave) => {
        if (!userId) {
            console.error('ConversationsContext: Cannot save conversation - no user ID');
            return;
        }

        const storageKey = `chatHistory_${userId}`;
        
        setConversations(prev => {
            const existingIndex = prev.findIndex(c => c.id === convoToSave.id);
            
            let metadataOnly;
            if (existingIndex > -1) {
                // Update existing conversation, preserve all fields that aren't being updated
                const existing = prev[existingIndex];
                metadataOnly = {
                    id: convoToSave.id,
                    title: convoToSave.title || existing.title || 'Untitled Chat',
                    lastUpdated: convoToSave.lastUpdated || new Date().toISOString(),
                    createdAt: existing.createdAt,
                    messageCount: existing.messageCount || 0,
                    fileCount: existing.fileCount || 0,
                    firstAssistantPreview: existing.firstAssistantPreview || '',
                    userId: userId
                };
            } else {
                // Add new conversation with default values for new fields
                metadataOnly = {
                    id: convoToSave.id,
                    title: convoToSave.title || 'Untitled Chat',
                    lastUpdated: convoToSave.lastUpdated || new Date().toISOString(),
                    createdAt: convoToSave.createdAt || new Date().toISOString(),
                    messageCount: 0,
                    fileCount: 0,
                    firstAssistantPreview: '',
                    userId: userId
                };
            }
            
            let updatedConversations;
            if (existingIndex > -1) {
                // Update existing conversation
                updatedConversations = [...prev];
                updatedConversations[existingIndex] = metadataOnly;
            } else {
                // Add new conversation at the beginning
                updatedConversations = [metadataOnly, ...prev];
            }

            // Limit stored conversations to 100 per user
            if (updatedConversations.length > 100) {
                updatedConversations = updatedConversations.slice(0, 100);
            }

            // Save to localStorage
            localStorage.setItem(storageKey, JSON.stringify(updatedConversations));
            console.log(`ConversationsContext: Saved conversation metadata for user ${userId}`);
            
            return updatedConversations;
        });
    }, [userId]);

    // --- Load Conversation Messages ---
    const loadConversation = async (convId) => {
        if (!isSessionValid) {
            throw new Error('Session expired. Please sign in again.');
        }

        console.log(`ConversationsContext: Loading conversation ${convId} for user ${userId}`);

        try {
            // Fetch messages from API (not from localStorage anymore)
            const messagesFromAPI = await apiChat.fetchConversationMessages(convId);
            
            // Convert API response to frontend format
            const formattedMessages = messagesFromAPI.map(msg => ({
                role: msg.role,
                content: msg.content || '[Content not available]',
                modelUsed: msg.model_used,
                files: msg.files,
                timestamp: msg.message_id ? msg.message_id.split('_')[0] : undefined,
                webSearchEnabled: msg.web_search_enabled || false,
                routingDetails: msg.routing_details || null // Map backend field to frontend property
            }));
            
            console.log(`ConversationsContext: Successfully loaded conversation ${convId} with ${formattedMessages.length} messages`);
            
            return {
                conversationId: convId,
                messages: formattedMessages
            };
            
        } catch (error) {
            console.error(`ConversationsContext: Failed to load conversation ${convId}:`, error);
            
            // Handle specific error cases
            if (error.message?.includes('Access denied')) {
                throw new Error('You do not have permission to view this conversation.');
            } else if (error.message?.includes('not found')) {
                // Auto-evict the stale entry so the sidebar stops showing it
                setConversations(prev => {
                    const updated = prev.filter(c => c.id !== convId);
                    localStorage.setItem(`chatHistory_${userId}`, JSON.stringify(updated));
                    return updated;
                });
                throw new Error('Conversation not found. It may have been deleted.');
            } else if (error.message?.includes('Authentication')) {
                setAuthError(error.message);
                setIsSessionValid(false);
                throw new Error('Your session has expired. Please sign in again.');
            } else {
                throw new Error(`Could not load conversation. ${error.message}`);
            }
        }
    };

    // --- Rename Conversation ---
    const handleRenameConversation = async (conversationId, newTitle) => {
        console.log(`ConversationsContext: User ${userId} renaming conversation ${conversationId}`);
        
        if (!newTitle || !newTitle.trim()) {
            console.error('ConversationsContext: Cannot rename - title is empty');
            throw new Error('Title cannot be empty');
        }

        if (!isSessionValid) {
            throw new Error('Session expired. Please sign in again.');
        }
        
        // Store original state for rollback
        const originalConversations = conversations;
        
        // Optimistic UI update
        setConversations(prev => prev.map(conv => 
            conv.id === conversationId 
                ? { ...conv, title: newTitle.trim(), lastUpdated: new Date().toISOString() }
                : conv
        ));
        
        // Update localStorage immediately for optimistic update
        const storageKey = `chatHistory_${userId}`;
        const updatedList = conversations.map(conv => 
            conv.id === conversationId 
                ? { ...conv, title: newTitle.trim(), lastUpdated: new Date().toISOString() }
                : conv
        );
        localStorage.setItem(storageKey, JSON.stringify(updatedList));
        
        try {
            // Call API to persist the change
            const updatedConversation = await apiChat.renameConversation(conversationId, newTitle);
            console.log(`ConversationsContext: Successfully renamed conversation ${conversationId}`);
            
            // Update with server response (in case there are server-side modifications)
            setConversations(prev => prev.map(conv => 
                conv.id === conversationId 
                    ? {
                        id: updatedConversation.conversation_id,
                        title: updatedConversation.title,
                        lastUpdated: updatedConversation.last_updated_at,
                        userId: userId
                    }
                    : conv
            ));
            
            // Update localStorage with server response
            const finalList = conversations.map(conv => 
                conv.id === conversationId 
                    ? {
                        id: updatedConversation.conversation_id,
                        title: updatedConversation.title,
                        lastUpdated: updatedConversation.last_updated_at,
                        userId: userId
                    }
                    : conv
            );
            localStorage.setItem(storageKey, JSON.stringify(finalList));
            
        } catch (error) {
            console.error(`ConversationsContext: Failed to rename conversation ${conversationId}:`, error);
            
            // Revert optimistic update on failure
            setConversations(originalConversations);
            localStorage.setItem(storageKey, JSON.stringify(originalConversations));
            
            // Handle authentication errors
            if (error.message?.includes('Authentication') || 
                error.message?.includes('401')) {
                setAuthError('Session expired. Please sign in again.');
                setIsSessionValid(false);
            }
            
            throw error; // Re-throw for UI error handling
        }
    };

    // --- Delete Conversation ---
    const handleDeleteConversation = async (conversationIdToDelete) => {
        console.log(`ConversationsContext: User ${userId} deleting conversation ${conversationIdToDelete}`);

        if (!isSessionValid) {
            throw new Error('Session expired. Please sign in again.');
        }
    
        // Store the conversation in case we need to revert
        const conversationToDelete = conversations.find(c => c.id === conversationIdToDelete);
        const originalConversations = conversations;
        
        // Optimistic UI update - remove from list immediately
        setConversations(prev => prev.filter(conv => conv.id !== conversationIdToDelete));
        
        // Update localStorage immediately
        const storageKey = `chatHistory_${userId}`;
        const updatedList = conversations.filter(conv => conv.id !== conversationIdToDelete);
        localStorage.setItem(storageKey, JSON.stringify(updatedList));
        
        try {
            // Call API to delete permanently
            await apiChat.deleteConversation(conversationIdToDelete);
            console.log(`ConversationsContext: Successfully deleted conversation ${conversationIdToDelete}`);
            
            // Force refresh localStorage to ensure consistency
            const finalList = conversations.filter(conv => conv.id !== conversationIdToDelete);
            localStorage.setItem(storageKey, JSON.stringify(finalList));
            setConversations(finalList);
            
            return { success: true, deletedId: conversationIdToDelete };
            
        } catch (error) {
            console.error(`ConversationsContext: Failed to delete conversation ${conversationIdToDelete}:`, error);
            
            // Revert optimistic update on failure
            setConversations(originalConversations);
            localStorage.setItem(storageKey, JSON.stringify(originalConversations));
            
            // Handle authentication errors
            if (error.message?.includes('Authentication') || 
                error.message?.includes('401')) {
                setAuthError('Session expired. Please sign in again.');
                setIsSessionValid(false);
            }
            
            throw error; // Re-throw for UI error handling
        }
    };

    // --- Refresh Conversations ---
    const refreshConversations = async () => {
        if (!userId || !isSessionValid) {
            console.log('ConversationsContext: Cannot refresh - no valid session');
            return;
        }

        try {
            setIsLoadingConversations(true);
            setConversationsError(null);
            
            const freshConversations = await apiChat.fetchConversations();
            
            const mappedConversations = freshConversations.map(conv => ({
                id: conv.conversation_id,
                title: conv.title || 'Untitled Chat',
                lastUpdated: conv.last_updated_at,
                createdAt: conv.created_at,
                messageCount: conv.message_count || 0,
                fileCount: conv.file_count || 0,
                firstAssistantPreview: conv.first_assistant_preview || '',
                userId: userId
            }));

            setConversations(mappedConversations);

            // Update cache
            const storageKey = `chatHistory_${userId}`;
            localStorage.setItem(storageKey, JSON.stringify(mappedConversations));
            
            console.log(`ConversationsContext: Refreshed ${mappedConversations.length} conversations`);
            
        } catch (error) {
            console.error('ConversationsContext: Failed to refresh conversations:', error);
            setConversationsError('Failed to refresh conversations');
            
            if (error.message?.includes('Authentication')) {
                setAuthError('Session expired. Please sign in again.');
                setIsSessionValid(false);
            }
        } finally {
            setIsLoadingConversations(false);
        }
    };

    const value = {
        // State
        conversations,
        isLoadingConversations,
        conversationsError,
        
        // Actions
        loadConversation,
        handleRenameConversation,
        handleDeleteConversation,
        saveConversation,
        refreshConversations
    };

    return (
        <ConversationsContext.Provider value={value}>
            {children}
        </ConversationsContext.Provider>
    );
};

export const useConversations = () => {
    const context = useContext(ConversationsContext);
    if (context === undefined) {
        throw new Error('useConversations must be used within a ConversationsProvider');
    }
    return context;
};